const DB_NAME = 'inspectpro-offline';
const DB_VERSION = 2;

export type CacheItemStatus = 'pending' | 'cached' | 'failed' | 'missing' | 'skipped';
export type CacheManifestStatus = 'pending' | 'completed' | 'partial' | 'failed';
export type CacheItemRequirement = 'required' | 'optional';

export interface OfflineCacheManifestItem {
    id: string;
    category: string;
    label: string;
    status: CacheItemStatus;
    required?: boolean;
    requirement?: CacheItemRequirement;
    ref_id?: string;
    parent_id?: string;
    url?: string;
    filename?: string;
    size?: number;
    error?: string;
    cached_at?: number;
    updated_at?: number;
}

export interface OfflineCacheManifestCategory {
    key: string;
    label: string;
    total: number;
    cached: number;
    failed: number;
    missing: number;
    pending: number;
    skipped: number;
    required: number;
    optional: number;
    size: number;
}

export interface OfflineCacheManifest {
    project_id: string;
    version: number;
    status: CacheManifestStatus;
    items: OfflineCacheManifestItem[];
    categories: OfflineCacheManifestCategory[];
    total_items: number;
    cached_items: number;
    failed_items: number;
    missing_items: number;
    pending_items: number;
    required_items: number;
    required_cached_items: number;
    required_failed_items: number;
    required_missing_items: number;
    required_pending_items: number;
    optional_items: number;
    optional_cached_items: number;
    optional_failed_items: number;
    optional_missing_items: number;
    optional_pending_items: number;
    blocking_items: number;
    optional_unavailable_items: number;
    diagnostic_ready: boolean;
    total_size: number;
    started_at?: number;
    completed_at?: number;
    updated_at: number;
}

interface StoreConfig {
    name: string;
    keyPath: string;
    autoIncrement?: boolean;
    indexes?: { name: string; keyPath: string; unique?: boolean }[];
}

const STORES: StoreConfig[] = [
    { name: 'auth', keyPath: 'id' },
    { name: 'projects', keyPath: 'id', indexes: [
        { name: 'status', keyPath: 'status' },
        { name: 'type', keyPath: 'type' },
        { name: 'category', keyPath: 'category' }
    ]},
    { name: 'facilities', keyPath: 'id', indexes: [
        { name: 'project_id', keyPath: 'project_id' }
    ]},
    { name: 'drawings', keyPath: 'id', indexes: [
        { name: 'project_id', keyPath: 'project_id' }
    ]},
    { name: 'facility_drawings', keyPath: 'id', indexes: [
        { name: 'facility_id', keyPath: 'facility_id' },
        { name: 'drawing_id', keyPath: 'drawing_id' }
    ]},
    { name: 'user_selections', keyPath: 'id', indexes: [
        { name: 'user_id', keyPath: 'user_id' },
        { name: 'project_id', keyPath: 'project_id' }
    ]},
    { name: 'damages', keyPath: 'drawing_id' },
    { name: 'annotations', keyPath: 'drawing_id' },
    { name: 'files', keyPath: 'id', indexes: [
        { name: 'type', keyPath: 'type' },
        { name: 'ref_id', keyPath: 'ref_id' }
    ]},
    { name: 'cache_manifests', keyPath: 'project_id', indexes: [
        { name: 'status', keyPath: 'status' },
        { name: 'updated_at', keyPath: 'updated_at' }
    ]},
    { name: 'sync_queue', keyPath: 'id', autoIncrement: true, indexes: [
        { name: 'status', keyPath: 'status' },
        { name: 'created_at', keyPath: 'created_at' },
        { name: 'entity_type', keyPath: 'entity_type' }
    ]},
    { name: 'meta', keyPath: 'key' }
];

export class OfflineStorage {
    private db: IDBDatabase | null = null;
    private dbReady: Promise<IDBDatabase>;

    constructor() {
        this.dbReady = this.initDB();
    }

    private initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                for (const store of STORES) {
                    if (!db.objectStoreNames.contains(store.name)) {
                        const os = db.createObjectStore(store.name, {
                            keyPath: store.keyPath,
                            autoIncrement: store.autoIncrement || false
                        });
                        if (store.indexes) {
                            for (const idx of store.indexes) {
                                os.createIndex(idx.name, idx.keyPath, { unique: idx.unique || false });
                            }
                        }
                    }
                }

                const tx = (event.target as IDBOpenDBRequest).transaction;
                if (tx) {
                    for (const store of STORES) {
                        if (!db.objectStoreNames.contains(store.name) || !store.indexes) continue;
                        const os = tx.objectStore(store.name);
                        for (const idx of store.indexes) {
                            if (!os.indexNames.contains(idx.name)) {
                                os.createIndex(idx.name, idx.keyPath, { unique: idx.unique || false });
                            }
                        }
                    }
                }
            };

            request.onsuccess = (event) => {
                this.db = (event.target as IDBOpenDBRequest).result;
                resolve(this.db);
            };

            request.onerror = (event) => {
                console.error('[OfflineStorage] DB open error', event);
                reject((event.target as IDBOpenDBRequest).error);
            };
        });
    }

    private async getDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;
        return this.dbReady;
    }

    async get(storeName: string, key: any): Promise<any> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async getAll(storeName: string): Promise<any[]> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
    }

    async getAllByIndex(storeName: string, indexName: string, value: any): Promise<any[]> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const index = store.index(indexName);
            const request = index.getAll(value);
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
    }

    async put(storeName: string, data: any): Promise<any> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.put(data);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async putMany(storeName: string, items: any[]): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            for (const item of items) {
                store.put(item);
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    async delete(storeName: string, key: any): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.delete(key);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async clear(storeName: string): Promise<void> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async count(storeName: string): Promise<number> {
        const db = await this.getDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.count();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async addToSyncQueue(item: {
        entity_type: string;
        entity_id: string;
        action: string;
        payload: any;
        api_endpoint?: string;
        operation_id?: string;
        client_id?: string;
        base_revision?: string | number;
        payload_hash?: string;
    }): Promise<number> {
        const all = await this.getAll('sync_queue');
        const existing = all.find((entry: any) => {
            if (!entry || entry.status === 'syncing') return false;
            if (entry.entity_type !== item.entity_type || entry.entity_id !== item.entity_id || entry.action !== item.action) {
                return false;
            }
            if (item.operation_id || entry.operation_id) {
                return String(entry.operation_id || '') === String(item.operation_id || '');
            }
            return item.entity_type === 'checklist_response';
        });
        if (existing) {
            await this.put('sync_queue', {
                ...existing,
                ...item,
                id: existing.id,
                created_at: existing.created_at || Date.now(),
                updated_at: Date.now(),
                status: 'pending',
                retry_count: 0,
                error: ''
            });
            return existing.id;
        }

        const queueItem = {
            ...item,
            created_at: Date.now(),
            updated_at: Date.now(),
            status: 'pending',
            retry_count: 0
        };
        return this.put('sync_queue', queueItem) as Promise<number>;
    }

    async getPendingSyncItems(): Promise<any[]> {
        const all = await this.getAllByIndex('sync_queue', 'status', 'pending');
        return all.sort((a: any, b: any) => a.created_at - b.created_at);
    }

    async getSyncQueueItems(): Promise<any[]> {
        const all = await this.getAll('sync_queue');
        return all.sort((a: any, b: any) => Number(a.created_at || 0) - Number(b.created_at || 0));
    }

    async getStoreCounts(): Promise<{ name: string; count: number; error?: string }[]> {
        const results: { name: string; count: number; error?: string }[] = [];
        for (const store of STORES) {
            try {
                results.push({ name: store.name, count: await this.count(store.name) });
            } catch (error: any) {
                results.push({ name: store.name, count: 0, error: error?.message || String(error) });
            }
        }
        return results;
    }

    async getCacheManifestSummaries(): Promise<any[]> {
        const manifests = await this.getAll('cache_manifests');
        return manifests
            .map((manifest: any) => this.normalizeCacheManifest(manifest))
            .sort((a: any, b: any) => Number(b.updated_at || 0) - Number(a.updated_at || 0));
    }

    async updateSyncItem(id: number, updates: any): Promise<void> {
        const item = await this.get('sync_queue', id);
        if (item) {
            Object.assign(item, updates);
            await this.put('sync_queue', item);
        }
    }

    async removeSyncItem(id: number): Promise<void> {
        await this.delete('sync_queue', id);
    }

    async setMeta(key: string, value: any): Promise<void> {
        await this.put('meta', { key, value, updated_at: Date.now() });
    }

    async getMeta(key: string): Promise<any> {
        const result = await this.get('meta', key);
        return result ? result.value : null;
    }

    async getStorageEstimate(): Promise<{ usage: number; quota: number }> {
        if (navigator.storage && navigator.storage.estimate) {
            const est = await navigator.storage.estimate();
            return { usage: est.usage || 0, quota: est.quota || 0 };
        }
        return { usage: 0, quota: 0 };
    }

    getEmptyCacheManifest(projectId: string): OfflineCacheManifest {
        return this.normalizeCacheManifest({
            project_id: projectId,
            version: 1,
            status: 'pending',
            items: [],
            categories: [],
            total_items: 0,
            cached_items: 0,
            failed_items: 0,
            missing_items: 0,
            pending_items: 0,
            required_items: 0,
            required_cached_items: 0,
            required_failed_items: 0,
            required_missing_items: 0,
            required_pending_items: 0,
            optional_items: 0,
            optional_cached_items: 0,
            optional_failed_items: 0,
            optional_missing_items: 0,
            optional_pending_items: 0,
            blocking_items: 0,
            optional_unavailable_items: 0,
            diagnostic_ready: false,
            total_size: 0,
            started_at: Date.now(),
            updated_at: Date.now()
        });
    }

    async getCacheManifest(projectId: string): Promise<OfflineCacheManifest | null> {
        const manifest = await this.get('cache_manifests', projectId);
        return manifest ? this.normalizeCacheManifest(manifest) : null;
    }

    async setCacheManifest(manifest: OfflineCacheManifest): Promise<void> {
        await this.put('cache_manifests', this.normalizeCacheManifest(manifest));
    }

    async updateCacheManifestItem(projectId: string, item: OfflineCacheManifestItem): Promise<OfflineCacheManifest> {
        const current = await this.getCacheManifest(projectId) || this.getEmptyCacheManifest(projectId);
        const now = Date.now();
        const items = Array.isArray(current.items) ? [...current.items] : [];
        const index = items.findIndex((entry: OfflineCacheManifestItem) => entry.id === item.id);
        const nextItem = {
            ...(index >= 0 ? items[index] : {}),
            ...item,
            updated_at: now
        };
        if (index >= 0) items[index] = nextItem;
        else items.push(nextItem);

        const next = this.normalizeCacheManifest({
            ...current,
            items,
            updated_at: now
        });
        await this.setCacheManifest(next);
        return next;
    }

    async getCacheManifestMissingItems(projectId: string, categories?: string[]): Promise<OfflineCacheManifestItem[]> {
        const manifest = await this.getCacheManifest(projectId);
        if (!manifest) return [];
        const categorySet = Array.isArray(categories) && categories.length > 0 ? new Set(categories) : null;
        return manifest.items.filter((item: OfflineCacheManifestItem) => {
            if (categorySet && !categorySet.has(item.category)) return false;
            return item.status === 'failed' || item.status === 'missing';
        });
    }

    private normalizeCacheManifest(manifest: any): OfflineCacheManifest {
        const items = Array.isArray(manifest?.items) ? manifest.items.map((item: any) => {
            const category = item.category || 'other';
            const required = this.isRequiredCacheItem({ ...item, category });
            return {
                ...item,
                category,
                status: item.status || 'pending',
                required,
                requirement: required ? 'required' : 'optional',
                size: Number(item.size || 0)
            };
        }) : [];
        const categoriesMap: { [key: string]: OfflineCacheManifestCategory } = {};

        for (const item of items) {
            const key = item.category || 'other';
            if (!categoriesMap[key]) {
                categoriesMap[key] = {
                    key,
                    label: this.getCacheCategoryLabel(key),
                    total: 0,
                    cached: 0,
                    failed: 0,
                    missing: 0,
                    pending: 0,
                    skipped: 0,
                    required: 0,
                    optional: 0,
                    size: 0
                };
            }
            categoriesMap[key].total += 1;
            if (item.status === 'cached') categoriesMap[key].cached += 1;
            if (item.status === 'failed') categoriesMap[key].failed += 1;
            if (item.status === 'missing') categoriesMap[key].missing += 1;
            if (item.status === 'pending') categoriesMap[key].pending += 1;
            if (item.status === 'skipped') categoriesMap[key].skipped += 1;
            if (item.required) categoriesMap[key].required += 1;
            else categoriesMap[key].optional += 1;
            if (item.status === 'cached') categoriesMap[key].size += Number(item.size || 0);
        }

        const isReady = (item: any) => item.status === 'cached' || item.status === 'skipped';
        const requiredItems = items.filter((item: any) => item.required);
        const optionalItems = items.filter((item: any) => !item.required);
        const cachedItems = items.filter((item: any) => isReady(item)).length;
        const failedItems = items.filter((item: any) => item.status === 'failed').length;
        const missingItems = items.filter((item: any) => item.status === 'missing').length;
        const pendingItems = items.filter((item: any) => item.status === 'pending').length;
        const requiredCachedItems = requiredItems.filter((item: any) => isReady(item)).length;
        const requiredFailedItems = requiredItems.filter((item: any) => item.status === 'failed').length;
        const requiredMissingItems = requiredItems.filter((item: any) => item.status === 'missing').length;
        const requiredPendingItems = requiredItems.filter((item: any) => item.status === 'pending').length;
        const optionalCachedItems = optionalItems.filter((item: any) => isReady(item)).length;
        const optionalFailedItems = optionalItems.filter((item: any) => item.status === 'failed').length;
        const optionalMissingItems = optionalItems.filter((item: any) => item.status === 'missing').length;
        const optionalPendingItems = optionalItems.filter((item: any) => item.status === 'pending').length;
        const blockingItems = requiredFailedItems + requiredMissingItems + requiredPendingItems;
        const optionalUnavailableItems = optionalFailedItems + optionalMissingItems + optionalPendingItems;
        const diagnosticReady = requiredItems.length > 0 && blockingItems === 0;
        let status: CacheManifestStatus = 'pending';
        if (diagnosticReady) status = 'completed';
        else if (requiredItems.length === 0 && items.length > 0 && failedItems === 0 && missingItems === 0 && pendingItems === 0) status = 'completed';
        else if (requiredFailedItems > 0 && requiredCachedItems === 0) status = 'failed';
        else if (items.length > 0) status = 'partial';

        return {
            project_id: String(manifest?.project_id || ''),
            version: Number(manifest?.version || 1),
            status,
            items,
            categories: Object.values(categoriesMap),
            total_items: items.length,
            cached_items: cachedItems,
            failed_items: failedItems,
            missing_items: missingItems,
            pending_items: pendingItems,
            required_items: requiredItems.length,
            required_cached_items: requiredCachedItems,
            required_failed_items: requiredFailedItems,
            required_missing_items: requiredMissingItems,
            required_pending_items: requiredPendingItems,
            optional_items: optionalItems.length,
            optional_cached_items: optionalCachedItems,
            optional_failed_items: optionalFailedItems,
            optional_missing_items: optionalMissingItems,
            optional_pending_items: optionalPendingItems,
            blocking_items: blockingItems,
            optional_unavailable_items: optionalUnavailableItems,
            diagnostic_ready: diagnosticReady,
            total_size: items.reduce((sum: number, item: any) => sum + (item.status === 'cached' ? Number(item.size || 0) : 0), 0),
            started_at: manifest?.started_at,
            completed_at: manifest?.completed_at,
            updated_at: Number(manifest?.updated_at || Date.now())
        };
    }

    private getCacheCategoryLabel(category: string): string {
        const labels: { [key: string]: string } = {
            project_data: '프로젝트 데이터',
            facility_data: '시설물 데이터',
            drawing_data: '도면 데이터',
            drawing_pdf: '도면 PDF',
            damage_photo: '손상 사진',
            overview_photo: '전경사진',
            checklist_photo: '체크리스트 사진',
            checklist_item: '체크리스트 항목',
            checklist_response: '체크리스트 응답',
            checklist_result: '체크리스트 결과',
            export: '마지막 export 산출물'
        };
        return labels[category] || category;
    }

    private isRequiredCacheItem(item: any): boolean {
        if (typeof item?.required === 'boolean') return item.required;
        if (item?.requirement === 'required') return true;
        if (item?.requirement === 'optional') return false;
        return !this.isOptionalCacheCategory(item?.category);
    }

    private isOptionalCacheCategory(category: string): boolean {
        return category === 'export';
    }
}

// Singleton
let _instance: OfflineStorage | null = null;

export function getOfflineStorage(): OfflineStorage {
    if (!_instance) {
        _instance = new OfflineStorage();
    }
    return _instance;
}
